import {handleActions} from 'redux-actions';
import * as actions from './actions';
import * as stickerItemsActions from "../sticker-items/actions";
import {IStickerItem} from "../sticker-items/reducer";

export interface ISticker {
    id: string;
    from?: string;
    settings?: object;
    is_my_llama?: boolean;
    scans_count?: number;
    new_scans_count?: number;
    sticker_items_limit?: number;
    configured_date?: string;
    stickerItems?: string[],
    type?: string;
}

export type FetchStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface IFetchState {
    status: FetchStatus,
    error: any
}

export interface IStickersState {
    byId: { [key: string]: ISticker },
    ids: string[],
    fetchState: { [key: string]: IFetchState },
    currentId?: string | null
}

const defaultState: IStickersState = {
    byId: {},
    ids: [],
    fetchState: {},
    // The sticker the user is currently viewing/creating on - set by every
    // sticker load request. Theming (memorial vs greeting) follows this.
    currentId: null
};

const reducer = handleActions(
    // @ts-ignore
    new Map([
        [actions.stickerLoadRequest,
            (state: IStickersState, action: { payload: { stickerId: string } }) => {
                const {stickerId} = action.payload;
                return {
                    ...state,
                    currentId: stickerId,
                    fetchState: {
                        ...state.fetchState,
                        [stickerId]: {
                            status: 'loading',
                            error: null
                        }
                    }
                }
            }],
        [actions.stickerLoadSuccess,
            (state: IStickersState, action: { payload: { stickerId: string, sticker: ISticker } }) => {
                const {stickerId, sticker} = action.payload;
                return {
                    ...state,
                    byId: {
                        ...state.byId,
                        [stickerId]: {
                            ...state.byId[stickerId],
                            ...sticker
                        }
                    },
                    ids: [...new Set([...state.ids, stickerId])],
                    fetchState: {
                        ...state.fetchState,
                        [stickerId]: {
                            status: 'succeeded',
                            error: null
                        }
                    }
                }
            }],
        [actions.stickerLoadFailed,
            (state: IStickersState, action: { payload: { stickerId: string, error: Error } }) => {
                const {stickerId, error} = action.payload;
                // Must live under fetchState (keyed like request/success do) -
                // this used to write a top-level key, so a failed load was
                // never observable and the status stayed 'loading' forever.
                return {
                    ...state,
                    fetchState: {
                        ...state.fetchState,
                        [stickerId]: {
                            status: 'failed',
                            error: error
                        }
                    }
                }
            }],
        [stickerItemsActions.stickerItemsLoadSuccess,
            (state: IStickersState, action: { payload: { stickerId: string, stickerItems: IStickerItem[] } }) => {
                const {stickerId, stickerItems} = action.payload;
                return {
                    ...state,
                    byId: {
                        ...state.byId,
                        [stickerId]: {
                            ...state.byId[stickerId],
                            stickerItems: stickerItems.map(item => item.id)
                        }
                    },
                }
            }],
        [stickerItemsActions.stickerItemDeleteRequest, (state: IStickersState, action: { payload: { stickerId: string, stickerItemId: string } }) => {
            const {stickerId, stickerItemId} = action.payload;
            let sticker = state.byId[stickerId];
            if (sticker && sticker.stickerItems && sticker.stickerItems.length > 0) {
                sticker = {
                    ...sticker,
                    stickerItems: [...sticker.stickerItems].filter(id => id !== stickerItemId)
                }
            }
            return {
                ...state,
                byId: {
                    ...state.byId,
                    [stickerId]: sticker
                }
            }
        }]
    ]),
    defaultState
);

export default reducer;
